import { Component, inject, OnInit, OnDestroy, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { LoginService } from './services/login-service/login.service';
import { LookUpService } from './services/lookup-service/lookup.service';
import { IdleTokenService } from './services/idle-token-service/idle-token.service';
import { ConnectionErrorService } from './services/connection-error-service/connection-error.service';
import { RouterOutlet } from '@angular/router';
import { SystemPageAccessTracker } from '../guard/system-page-access-tracker';
import { SwUpdate } from '@angular/service-worker';
import { Toast } from './shared-components/toast/toast';
import { Loader } from './shared-components/loader/loader';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Toast, Loader],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('design-app');
  private tracker = inject(SystemPageAccessTracker);
  private idleTokenService = inject(IdleTokenService);
  private connectionErrorService = inject(ConnectionErrorService);
  protected readonly errorToast = this.connectionErrorService.errorToast;
  private swUpdate = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly workspaceLoading = inject(LoginService).workspaceLoading;
  private _userResolved = false;
  lookUpService = inject(LookUpService);
  private removeGlobalErrorListeners: (() => void) | null = null;

  constructor(
    private authService: MsalService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.removeGlobalErrorListeners = this.connectionErrorService.registerGlobalListeners();
    this.setupServiceWorkerAutoUpdate();

    this.authService
      .handleRedirectObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result && result.account) {
            this.authService.instance.setActiveAccount(result.account);
            this.getUser(result);
            // Start idle monitoring after successful login
            this.idleTokenService.startIdleMonitoring();
          } else {
            this.resolveFromCachedSession();
          }
        },
        error: (err) => console.error('[handleRedirectObservable] error:', err),
      });
  }

  private setupServiceWorkerAutoUpdate(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.type !== 'VERSION_READY') {
          return;
        }

        const lastReload = Number(sessionStorage.getItem('swLastAutoReload') || 0);
        if (Date.now() - lastReload < 60_000) {
          return;
        }
        sessionStorage.setItem('swLastAutoReload', String(Date.now()));

        
        this.swUpdate.activateUpdate().finally(() => window.location.reload());
      });
  }

  private resolveFromCachedSession(): void {
    const activeAccount =
      this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()[0];

    if (!activeAccount) return; // not logged in — MsalGuard will handle protected routes

    this.authService.instance.setActiveAccount(activeAccount);

    this.authService
      .acquireTokenSilent({
        account: activeAccount,
        scopes: ['User.Read'],
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tokenResponse) => {
          this.getUser(tokenResponse);
          // Start idle monitoring after successful token acquisition
          this.idleTokenService.startIdleMonitoring();
        },
        error: (err) => {
          console.error('[acquireTokenSilent] error:', err);
          const returnUrl =
            sessionStorage.getItem('app-return-url') ?? window.location.href;
          this.authService.loginRedirect({
            scopes: ['User.Read'],
            redirectStartPage: returnUrl,
          });
        },
      });
  }

  getUser(profile: any) {
    if (this._userResolved) return;
    this._userResolved = true;
    sessionStorage.removeItem('app-return-url');

    const idToken = profile?.idToken ?? profile?.idTokenClaims ?? null;
    localStorage.setItem('tokenId', JSON.stringify(idToken));

    if (this.shouldRedirectToWorkspace()) {
      this.router.navigateByUrl('/workspace');
    }
  }

  private shouldRedirectToWorkspace(): boolean {
    const path = window.location.pathname;
    return path === '/' || path === '';
  }

  private redirectToUnauthorized(): void {
    this.router.navigateByUrl('/unauthorized', {
      state: { reason: 'not-authorized' },
    });
  }

  // Cleanup on component destroy
  ngOnDestroy(): void {
    this.removeGlobalErrorListeners?.();
    this.idleTokenService.stopIdleMonitoring();
  }

  clearErrorToast(): void {
    this.connectionErrorService.clearErrorToast();
  }
}